import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, X, Star, CalendarPlus } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { web, errMsg, recruiterCalendarUrl } from "../../lib/api";
import { PageHeader, Loader, ErrorNote, Empty, Btn } from "../../components/app/kit";
import { CalendarLink } from "../../components/app/CalendarLink";

const dayKey = (g) => {
  try { return new Date(g.start).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", timeZone: g.timezone || undefined }); } catch { return String(g.start).slice(0, 10); }
};
const timeOf = (g) => {
  try { return new Date(g.start).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: g.timezone || undefined, timeZoneName: "short" }); } catch { return ""; }
};

/** Follow / unfollow a team's schedule (used on prospect pages). */
export const FollowTeamButton = ({ teamId, teamName }) => {
  const { data, reload } = useApi(teamId && "/recruiter/follows", web);
  const [busy, setBusy] = useState(false);
  if (!teamId || !data) return null;
  const on = Array.isArray(data) && data.some((f) => f.team_id === teamId);
  const toggle = async () => {
    setBusy(true);
    try {
      if (on) await web.delete(`/recruiter/follows/${teamId}`);
      else await web.post("/recruiter/follows", { team_id: teamId, team_name: teamName });
      toast.success(on ? "Unfollowed team" : `Following ${teamName ?? "team"}. Their games show on your Schedule.`);
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Btn variant={on ? "ghost" : "lime"} disabled={busy} onClick={toggle} data-testid="follow-team-button">
      <Star className={`h-4 w-4 ${on ? "fill-[#39FF14] text-[#39FF14]" : ""}`} />{on ? "Following team" : "Follow team"}
    </Btn>
  );
};

const GameRow = ({ g }) => (
  <li data-testid={`rs-game-${g.id}`} className="flex flex-wrap items-start gap-4 rounded-2xl border border-[#1E293B] bg-[#0F172A] px-5 py-4">
    <div className="shrink-0 font-mono-gd text-sm font-bold text-[#39FF14] sm:w-24">{timeOf(g)}</div>
    <div className="min-w-0 flex-1 basis-56">
      <p className="break-words font-display text-xl font-bold uppercase text-white">{g.team_name} <span className="text-slate-400">vs</span> {g.opponent ?? g.title ?? "TBA"}</p>
      {g.location && <p className="flex items-center gap-1.5 text-xs text-slate-300"><MapPin className="h-3.5 w-3.5" />{g.location}</p>}
      {String(g.status ?? "").toLowerCase().startsWith("cancel") && <p className="font-mono-gd text-xs uppercase text-red-300">Cancelled</p>}
      {g.players?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {g.players.map((p) => <Link key={p.id} to={`/recruiter/prospects/${p.id}`} className="rounded-full bg-[#39FF14]/15 px-2.5 py-1 font-mono-gd text-[10px] uppercase text-[#39FF14] hover:bg-[#39FF14]/25">{p.name}</Link>)}
        </div>
      )}
    </div>
  </li>
);

export default function RecruiterSchedule() {
  const { data, loading, error, reload } = useApi("/recruiter/schedule", web);
  const [cal, setCal] = useState(false);
  const games = Array.isArray(data?.games) ? data.games : [];
  const teams = Array.isArray(data?.teams) ? data.teams : [];
  const groups = games.reduce((acc, g) => { const k = dayKey(g); (acc[k] ??= []).push(g); return acc; }, {});
  const unfollow = async (t) => {
    try { await web.delete(`/recruiter/follows/${t.team_id}`); toast.success("Unfollowed"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Teams you follow and teams of players on your board" title="Schedule">
        <Btn data-testid="rs-calendar-button" onClick={() => setCal(true)}><CalendarPlus className="h-4 w-4" />Add to my calendar</Btn>
      </PageHeader>
      <CalendarLink open={cal} onClose={() => setCal(false)} path="/recruiter/calendar-link" urlFor={recruiterCalendarUrl} title="Your recruiting calendar"
        description="Every game from teams you follow and teams of players on your board, in your phone or Google calendar. New teams and schedule changes show up automatically."
        copied="Link copied. Keep it private: anyone with it can see your schedule." resetNote="Calendars using the old link stop updating until you add the new one." />
      {loading && !data ? <Loader /> : error ? <ErrorNote error={error} /> : (
        <div className="space-y-8">
          <div data-testid="rs-teams" className="flex flex-wrap gap-2">
            {!teams.length ? <p className="text-sm text-slate-300">Add players to your board or tap Follow team on a prospect page to see games here.</p> : teams.map((t) => (
              <span key={t.team_id} data-testid={`rs-team-${t.team_id}`} className="inline-flex items-center gap-2 rounded-full border border-[#1E293B] bg-[#0F172A] px-3.5 py-1.5 text-sm text-white">
                {t.followed ? <Star className="h-3.5 w-3.5 fill-[#39FF14] text-[#39FF14]" /> : <span className="font-mono-gd text-[10px] uppercase text-slate-400">Board</span>}
                {t.team_name ?? "Team"}
                {t.followed && <button onClick={() => unfollow(t)} data-testid={`rs-unfollow-${t.team_id}`} aria-label="Unfollow" className="text-slate-400 hover:text-red-300"><X className="h-3.5 w-3.5" /></button>}
              </span>
            ))}
          </div>
          {Array.isArray(data?.unavailable) && data.unavailable.length > 0 && (
            <p data-testid="rs-unavailable" className="text-xs text-amber-300">
              Schedule not available right now for: {data.unavailable.map((id) => teams.find((t) => t.team_id === id)?.team_name ?? "a team").join(", ")}.
            </p>
          )}
          {!games.length ? <Empty title="No upcoming games" text="Games from these teams' GAMEDAY schedules show here as soon as coaches add them." /> : Object.entries(groups).map(([day, list]) => (
            <section key={day}>
              <h2 className="mb-3 flex items-center gap-2 font-mono-gd text-xs uppercase tracking-[0.2em] text-slate-400"><CalendarDays className="h-4 w-4 text-[#39FF14]" />{day}</h2>
              <ul className="space-y-3">{list.map((g) => <GameRow key={`${g.team_id}-${g.id}`} g={g} />)}</ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
